import { Flame } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Etiqueta } from '@/features/tickets/badges';
import {
  ROTULO_DE_MOTIVO,
  ROTULO_DE_SITUACAO_DE_ERRO,
  type MotivoDaSugestao,
  type SituacaoDeErro,
} from '@/features/kedb/tipos';

/** As etiquetas da Base de Erros Conhecidos. */

const ESTILO_DE_SITUACAO: Record<SituacaoDeErro, string> = {
  ACTIVE: 'bg-warning/15 text-warning ring-warning/30',
  RESOLVED: 'bg-success/12 text-success ring-success/25',
  RETIRED: 'bg-muted text-muted-foreground ring-border',
};

export function EtiquetaDeSituacaoDeErro({ situacao }: { situacao: SituacaoDeErro }) {
  return (
    <Etiqueta className={ESTILO_DE_SITUACAO[situacao]}>
      {ROTULO_DE_SITUACAO_DE_ERRO[situacao]}
    </Etiqueta>
  );
}

/**
 * Quantos incidentes este erro já causou.
 *
 * É o número que justifica priorizar a correção definitiva, e por isso ele não
 * é um dado escondido numa ficha: aparece na lista, na sugestão e no cabeçalho.
 * Zero não vira etiqueta — um contador em zero só ocuparia espaço.
 */
export function Recorrencia({ contagem }: { contagem: number }) {
  if (contagem <= 0) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
        contagem >= 5
          ? 'bg-destructive/12 text-destructive ring-destructive/25'
          : 'bg-secondary text-secondary-foreground ring-border',
      )}
      title="Incidentes em que a solução de contorno foi aplicada. O contador é mantido pelo banco, por trigger."
    >
      <Flame className="h-3 w-3" aria-hidden />
      {contagem} incidente{contagem === 1 ? '' : 's'}
    </span>
  );
}

/**
 * Por que a Central sugeriu este erro.
 *
 * O motivo é tão importante quanto a sugestão: "porque o relato se parece" e
 * "porque é o mesmo switch" pedem confianças diferentes de quem lê, e sem eles
 * a pontuação seria um número sem procedência.
 */
export function MotivosDaSugestao({ motivos }: { motivos: readonly MotivoDaSugestao[] }) {
  if (motivos.length === 0) return null;
  return (
    <span className="text-xs text-muted-foreground">
      {motivos.map((motivo) => ROTULO_DE_MOTIVO[motivo]).join(' · ')}
    </span>
  );
}
